/**
 * Invite Send Page
 *
 * Form for sending new invites.
 *
 * @fileoverview Admin invite send page
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdChevronRight } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import { InviteForm } from '../../../components/admin';
import { useSendInvite } from '../../../hooks/useAdminInvites';
import type { CreateInviteInput } from '../../../types/admin';

/**
 * Invite Send Page Component
 *
 * Page for sending a new invite.
 */
export const InviteSend: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { sendInvite, loading, error, clearError } = useSendInvite();

  const handleSubmit = async (data: CreateInviteInput) => {
    clearError();
    const invite = await sendInvite(data);
    if (invite) {
      toast({
        title: 'Invite sent',
        description: `An invitation has been sent to ${invite.email}.`,
        status: 'success',
        duration: 5000,
      });
      navigate('/admin/invites');
    }
  };

  const handleCancel = () => {
    navigate('/admin/invites');
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Breadcrumb */}
      <Breadcrumb separator={<Icon as={MdChevronRight} color="gray.400" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/admin/invites">
            Invites
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Send Invite</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg">Send Invite</Heading>
          <Text color="gray.500" mt={1}>
            Send an invitation to a new user
          </Text>
        </Box>
        <Button
          variant="ghost"
          leftIcon={<Icon as={MdArrowBack} />}
          onClick={() => navigate('/admin/invites')}
        >
          Back to Invites
        </Button>
      </HStack>

      {/* Form */}
      <InviteForm
        loading={loading}
        error={error}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </VStack>
  );
};

export default InviteSend;
